import { useState, useRef } from 'react';

import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { formClasses } from '../utils/FormStyles';
import { Label } from '../utils/Label';

import {
  ComboboxInput,
  ComboboxTransition,
  EmptyResults,
  SelectedTag,
  SingleColumnLayout,
  TooltipDisplay,
  TwoColumnLayout,
} from './components';
import {
  CategorizedComboboxFieldProps,
  CategorizedComboboxOption,
} from './types';
import {
  filterOptions,
  flattenOptions,
  groupOptions,
  handleSelectionChange,
  normalizeValue,
  countSelectedItemsByDomain,
} from './utils';

export function CategorizedComboboxField({
  value,
  onChange,
  options,
  label,
  errorMessage,
  id,
  placeholder = 'Select an option',
  className,
  disabled = false,
  link = '',
  tooltip = '',
  twoColumnLayout = false,
  multiSelect = false,
  displaySelectedCount = true,
  maxSelections = Infinity,
  maximumDomainItems,
  subLabel,
}: CategorizedComboboxFieldProps) {
  const [query, setQuery] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const buttonRef = useRef(null);

  // Process options
  const allOptions = flattenOptions(options);
  const filteredOptions = filterOptions(allOptions, query);
  const groupedOptions = groupOptions(filteredOptions);

  // Normalize selected values for consistent handling
  const selectedValues = normalizeValue(value, multiSelect);
  const canAddMore = selectedValues.length < maxSelections;

  // Get domain counts for displaying and validation
  const domainCounts = countSelectedItemsByDomain(selectedValues);

  // Reset query function to clear input after selection
  const resetQuery = () => setQuery('');

  const handleChange = (
    selected: CategorizedComboboxOption | CategorizedComboboxOption[],
  ) => {
    handleSelectionChange(
      selected,
      multiSelect,
      selectedValues,
      maxSelections,
      onChange,
      resetQuery,
      maximumDomainItems,
    );
  };

  const removeSelectedItem = (itemToRemove: CategorizedComboboxOption) => {
    if (!itemToRemove?.value) return;

    if (multiSelect && Array.isArray(value)) {
      onChange(
        value.filter(
          (item) =>
            item?.value !== undefined && item.value !== itemToRemove.value,
        ),
      );
    }
  };

  const handleOptionToggle = (option: CategorizedComboboxOption) => {
    if (!option?.value) return;

    if (multiSelect) {
      if (Array.isArray(value)) {
        onChange(
          value.filter(
            (item) => item?.value !== undefined && item.value !== option.value,
          ),
        );
      }
    } else {
      onChange({} as CategorizedComboboxOption);
    }
  };

  const inputClassName = clsx(
    formClasses,
    'pr-10 h-11',
    { 'cursor-not-allowed': disabled },
    { 'border-red-500 focus:border-red-700': errorMessage },
  );

  return (
    <div className={className}>
      {label && (
        <Label id={id} link={link}>
          {label}
        </Label>
      )}

      {subLabel && (
        <label className="mb-3 block text-base italic text-gray-700">
          {subLabel}
        </label>
      )}

      {multiSelect ? (
        <Combobox
          multiple
          disabled={disabled}
          value={Array.isArray(value) ? value : []}
          onChange={handleChange}
        >
          <div
            className="relative flex w-full flex-1"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div className="relative h-full w-full">
              <div
                className={clsx(
                  formClasses,
                  { "pr-14": displaySelectedCount },
                  'flex h-full h-fit w-full flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white text-gray-900 pr-8',
                )}
              >
                {Array.isArray(value) &&
                  value.length > 0 &&
                  value
                    .filter((item) => item?.value !== undefined)
                    .map((item) => (
                      <SelectedTag
                        key={`selected-${String(item.value)}`}
                        disabled={disabled}
                        item={item}
                        onRemove={removeSelectedItem}
                      />
                    ))}
                {canAddMore && (
                  <div className="flex-1">
                    <Combobox.Input
                      className="w-full border-none p-0 outline-none focus:ring-0"
                      displayValue={() => query}
                      placeholder={
                        Array.isArray(value) && value.length ? '' : placeholder
                      }
                      onChange={(e) => setQuery(e.target.value)}
                      onClick={() => buttonRef.current?.click()}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Backspace' &&
                          query === '' &&
                          Array.isArray(value) &&
                          value.length > 0
                        ) {
                          e.preventDefault();
                          const lastItem = value[value.length - 1];
                          if (lastItem) {
                            removeSelectedItem(lastItem);
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              <Combobox.Button
                ref={buttonRef}
                className="absolute inset-y-0 right-0 flex items-center pr-2"
              >
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-gray-400"
                />
              </Combobox.Button>

              {maxSelections !== Infinity && displaySelectedCount && (
                <div className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {selectedValues.length} / {maxSelections}
                </div>
              )}

              {tooltip && showTooltip && <TooltipDisplay tooltip={tooltip} />}
            </div>

            <ComboboxTransition setQuery={setQuery}>
              <Combobox.Options className="absolute z-10 mt-12 max-h-80 w-full overflow-hidden rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {filteredOptions.length === 0 && query !== '' ? (
                  <EmptyResults />
                ) : twoColumnLayout ? (
                  <TwoColumnLayout
                    canAddMore={canAddMore}
                    domainCounts={domainCounts}
                    groupedOptions={groupedOptions}
                    maximumDomainItems={maximumDomainItems}
                    multiSelect={multiSelect}
                    selectedValues={selectedValues}
                    setShowTooltip={setShowTooltip}
                    onOptionToggle={handleOptionToggle}
                  />
                ) : (
                  <SingleColumnLayout
                    canAddMore={canAddMore}
                    domainCounts={domainCounts}
                    groupedOptions={groupedOptions}
                    maximumDomainItems={maximumDomainItems}
                    multiSelect={multiSelect}
                    selectedValues={selectedValues}
                    setShowTooltip={setShowTooltip}
                    onOptionToggle={handleOptionToggle}
                  />
                )}
              </Combobox.Options>
            </ComboboxTransition>
          </div>
        </Combobox>
      ) : (
        <Combobox
          disabled={disabled}
          value={Array.isArray(value) ? value[0] : value}
          onChange={handleChange}
        >
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <ComboboxInput
              className={className}
              inputClassName={inputClassName}
              maxSelections={maxSelections}
              placeholder={placeholder}
              selectedValues={selectedValues}
              setQuery={setQuery}
              displayValue={(option: unknown) => {
                const typedOption = option as CategorizedComboboxOption;
                return typedOption?.name || '';
              }}
            />

            {tooltip && showTooltip && <TooltipDisplay tooltip={tooltip} />}

            <ComboboxTransition setQuery={setQuery}>
              <Combobox.Options className="absolute z-10 mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {filteredOptions.length === 0 && query !== '' ? (
                  <EmptyResults />
                ) : twoColumnLayout ? (
                  <TwoColumnLayout
                    canAddMore={canAddMore}
                    domainCounts={domainCounts}
                    groupedOptions={groupedOptions}
                    maximumDomainItems={maximumDomainItems}
                    multiSelect={multiSelect}
                    selectedValues={selectedValues}
                    setShowTooltip={setShowTooltip}
                    onOptionToggle={handleOptionToggle}
                  />
                ) : (
                  <SingleColumnLayout
                    canAddMore={canAddMore}
                    domainCounts={domainCounts}
                    groupedOptions={groupedOptions}
                    maximumDomainItems={maximumDomainItems}
                    multiSelect={multiSelect}
                    selectedValues={selectedValues}
                    setShowTooltip={setShowTooltip}
                    onOptionToggle={handleOptionToggle}
                  />
                )}
              </Combobox.Options>
            </ComboboxTransition>
          </div>
        </Combobox>
      )}

      {errorMessage && (
        <p className="mt-2 text-sm text-red-600" id={`${id}-error`}>
          {typeof errorMessage === 'object'
            ? errorMessage.message
            : errorMessage}
        </p>
      )}
    </div>
  );
}

export * from './types';
export * from './utils';
export * from './components';
